// SecuritySettings.tsx - Security settings component
import { useState } from 'react';
import {
  Paper,
  Stack,
  PasswordInput,
  Button,
  Text,
  Title,
  Alert,
  Divider,
  Group,
  Modal,
  Progress,
} from '@mantine/core';
import {
  IconLock,
  IconKey,
  IconCheck,
  IconAlertCircle,
  IconTrash,
  IconShield,
  IconMail,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

export function SecuritySettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Change Password
  const [changePasswordData, setChangePasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Delete Account
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  const getPasswordStrength = (pwd: string): number => {
    let strength = 0;
    if (pwd.length >= 8) strength += 25;
    if (pwd.length >= 12) strength += 25;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength += 25;
    if (/\d/.test(pwd)) strength += 12.5;
    if (/[^a-zA-Z0-9]/.test(pwd)) strength += 12.5;
    return strength;
  };

  const passwordStrength = getPasswordStrength(changePasswordData.newPassword);
  const getStrengthColor = () => {
    if (passwordStrength < 50) return 'red';
    if (passwordStrength < 75) return 'yellow';
    return 'green';
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (changePasswordData.newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://carleads-api-staging.carleads.workers.dev'}/api/users/change-password`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            currentPassword: changePasswordData.currentPassword,
            newPassword: changePasswordData.newPassword,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setChangePasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        setError(data.error || 'Failed to change password');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setError(null);

    if (!deletePassword) {
      setError('Password is required');
      setDeleting(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://carleads-api-staging.carleads.workers.dev'}/api/users/account`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ password: deletePassword }),
        }
      );

      const data = await response.json();

      if (data.success) {
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        setError(data.error || 'Failed to delete account');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Paper p="xl" radius="md" withBorder>
      <Stack gap="lg">
        <div>
          <Title order={2} mb="xs">
            Security Settings
          </Title>
          <Text c="dimmed" size="sm">
            Manage your account security and password.
          </Text>
        </div>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
            {error}
          </Alert>
        )}

        {success && (
          <Alert icon={<IconCheck size={16} />} color="green" title="Success">
            Password changed successfully!
          </Alert>
        )}

        {/* Change Password */}
        <div>
          <Group justify="space-between" mb="md">
            <div>
              <Text size="sm" fw={600}>
                Change Password
              </Text>
              <Text size="xs" c="dimmed">
                Update your password to keep your account secure
              </Text>
            </div>
            <IconKey size={24} />
          </Group>

          <form onSubmit={handleChangePassword}>
            <Stack gap="md">
              <PasswordInput
                label="Current Password"
                placeholder="Enter current password"
                required
                value={changePasswordData.currentPassword}
                onChange={(e) =>
                  setChangePasswordData({
                    ...changePasswordData,
                    currentPassword: e.target.value,
                  })
                }
                leftSection={<IconLock size={16} />}
                disabled={loading}
              />

              <div>
                <PasswordInput
                  label="New Password"
                  placeholder="Enter new password"
                  required
                  value={changePasswordData.newPassword}
                  onChange={(e) =>
                    setChangePasswordData({
                      ...changePasswordData,
                      newPassword: e.target.value,
                    })
                  }
                  leftSection={<IconKey size={16} />}
                  disabled={loading}
                />
                {changePasswordData.newPassword && (
                  <div style={{ marginTop: 8 }}>
                    <Progress
                      value={passwordStrength}
                      color={getStrengthColor()}
                      size="sm"
                      mb={4}
                    />
                    <Text size="xs" c="dimmed">
                      Password strength:{' '}
                      {passwordStrength < 50
                        ? 'Weak'
                        : passwordStrength < 75
                        ? 'Medium'
                        : 'Strong'}
                    </Text>
                  </div>
                )}
              </div>

              <PasswordInput
                label="Confirm New Password"
                placeholder="Confirm new password"
                required
                value={changePasswordData.confirmPassword}
                onChange={(e) =>
                  setChangePasswordData({
                    ...changePasswordData,
                    confirmPassword: e.target.value,
                  })
                }
                leftSection={<IconLock size={16} />}
                disabled={loading}
                error={
                  changePasswordData.confirmPassword &&
                  changePasswordData.newPassword !== changePasswordData.confirmPassword
                    ? 'Passwords do not match'
                    : null
                }
              />

              <Button type="submit" loading={loading} leftSection={<IconKey size={16} />}>
                Change Password
              </Button>
            </Stack>
          </form>
        </div>

        <Divider />

        {/* Email Verification Status */}
        <div>
          <Group justify="space-between" mb="md">
            <div>
              <Text size="sm" fw={600}>
                Email Verification
              </Text>
              <Text size="xs" c="dimmed">
                Verify your email address for account security
              </Text>
            </div>
            <IconMail size={24} />
          </Group>
          <Button
            variant="outline"
            leftSection={<IconMail size={16} />}
            onClick={() => {
              // This would typically open a modal or navigate
              // For now, we'll just show a message
              alert('Email verification status check - to be implemented');
            }}
          >
            Check Verification Status
          </Button>
        </div>

        <Divider />

        {/* Delete Account */}
        <div>
          <Group justify="space-between" mb="md">
            <div>
              <Text size="sm" fw={600} c="red">
                Delete Account
              </Text>
              <Text size="xs" c="dimmed">
                Permanently delete your account and all data
              </Text>
            </div>
            <IconTrash size={24} color="red" />
          </Group>
          <Button
            variant="outline"
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={() => setDeleteAccountModalOpen(true)}
          >
            Delete Account
          </Button>
        </div>

        {/* Delete Account Modal */}
        <Modal
          opened={deleteAccountModalOpen}
          onClose={() => setDeleteAccountModalOpen(false)}
          title="Delete Account"
          centered
        >
          <Stack gap="md">
            <Alert icon={<IconAlertCircle size={16} />} color="red" title="Warning">
              This action cannot be undone. All your data will be permanently deleted.
            </Alert>

            <PasswordInput
              label="Confirm Password"
              placeholder="Enter your password"
              required
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              leftSection={<IconLock size={16} />}
              disabled={deleting}
            />

            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
                {error}
              </Alert>
            )}

            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={() => {
                  setDeleteAccountModalOpen(false);
                  setDeletePassword('');
                  setError(null);
                }}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                color="red"
                leftSection={<IconTrash size={16} />}
                onClick={handleDeleteAccount}
                loading={deleting}
              >
                Delete Account
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Paper>
  );
}
